import { Component, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  ICreateUbicacion,
  IGeoJSONPoint,
  IUbicacionBase,
  IUpdateUbicacion,
  IValoresUbicacionHospital,
} from 'modelos/src';
import { AuxiliaresModule } from '../../../../../../auxiliares/auxiliares.module';
import { MapaUbicarComponent } from '../../../../../../auxiliares/mapas/mapa-ubicar/mapa-ubicar.component';
import { HelperService } from '../../../../../../auxiliares/servicios/helper.service';
import { UbicacionService } from '../../../../../../auxiliares/servicios/http/ubicacion.service';
import { LoginService } from '../../../../../login/login.service';

type IHospital = IUbicacionBase<'Hospital'>;

@Component({
  selector: 'app-crear-editar-hospital',
  imports: [AuxiliaresModule, MapaUbicarComponent],
  templateUrl: './crear-editar-hospital.component.html',
  styleUrl: '../../../form-dialogo.scss',
})
export class CrearEditarHospitalComponent implements OnInit {
  helper = inject(HelperService);
  private service = inject(UbicacionService);
  // Siempre en diálogo: { edit: hospital } edita; sin datos, crea.
  data = inject<{ edit?: IHospital } | null>(MAT_DIALOG_DATA, {
    optional: true,
  });
  dialogRef = inject<MatDialogRef<CrearEditarHospitalComponent>>(MatDialogRef);

  public id?: string | null;
  public loading = true;
  public guardando = false;
  public form?: FormGroup;
  public title = '';
  public subtitle?: string;
  public hospital?: IHospital;
  public gestiones: IValoresUbicacionHospital['gestion'][] = [
    'Público',
    'Privado',
    'Público-privado',
  ];
  public mostrarCliente = HelperService.getTipoCliente() !== 'Final';

  public centrarA?: IGeoJSONPoint;
  public direccion?: string;

  private createForm(): void {
    const h = this.hospital;
    this.form = new FormGroup({
      identificacion: new FormControl(h?.identificacion, Validators.required),
      idCliente: new FormControl(
        h?.idCliente ?? LoginService.getCliente()?._id,
      ),
      gestion: new FormControl(h?.valores?.gestion),
      activo: new FormControl(this.id ? !!h?.valores?.activo : true),
      telefono: new FormControl(h?.valores?.telefono),
      email: new FormControl(h?.valores?.email, Validators.email),
      direccion: new FormControl(h?.direccion, Validators.required),
      geojson: new FormControl(h?.geojson, Validators.required),
    });
  }

  private getData(): ICreateUbicacion | IUpdateUbicacion {
    const v = this.form!.value;
    const valores: IValoresUbicacionHospital = {
      telefono: v.telefono || undefined,
      email: v.email || undefined,
      gestion: v.gestion || undefined,
      activo: !!v.activo,
    };
    const data: ICreateUbicacion = {
      categoria: 'Hospital',
      identificacion: v.identificacion?.trim(),
      direccion: v.direccion,
      geojson: v.geojson,
      valores,
    };
    if (!this.id && this.mostrarCliente && v.idCliente) {
      data.idCliente = v.idCliente;
    }
    return data;
  }

  public async onSubmit(): Promise<void> {
    if (!this.form?.valid) return;
    this.guardando = true;
    try {
      if (this.id) {
        await this.service.update(this.id, this.getData() as IUpdateUbicacion);
        const actualizado = (await this.service.getById(this.id)) as IHospital;
        this.helper.notifSuccess('Hospital actualizado correctamente');
        this.close(actualizado);
      } else {
        const creado = (await this.service.create(
          this.getData() as ICreateUbicacion,
        )) as IHospital;
        this.helper.notifSuccess('Hospital creado correctamente');
        this.close(creado);
      }
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    } finally {
      this.guardando = false;
    }
  }

  public close(hospital?: IHospital): void {
    this.dialogRef.close(hospital);
  }

  public patchDireccion(direccion: string): void {
    this.direccion = direccion;
    this.form?.patchValue({ direccion });
  }

  public patchGeoJSON(geojson: IGeoJSONPoint): void {
    this.centrarA = geojson;
    this.form?.patchValue({ geojson });
  }

  async ngOnInit(): Promise<void> {
    try {
      this.id = this.data?.edit?._id;
      if (this.id) {
        this.hospital = (await this.service.getById(this.id)) as IHospital;
        this.centrarA = this.hospital?.geojson as IGeoJSONPoint;
        this.direccion = this.hospital?.direccion;
      }
      this.title = this.id ? 'Editar hospital' : 'Crear hospital';
      this.subtitle = this.hospital?.identificacion;
      this.createForm();
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    } finally {
      this.loading = false;
    }
  }
}
